/**
 * io.ai in Word: the same bid agent as the web app, inside the task pane.
 *
 * Two screens. Home asks which tender the document is for and is shown
 * whenever the document is not linked (or the user asks to switch). The
 * workspace is the header (tender, account), the conversation bar (name,
 * history, new chat) and the thread. The agent gets a snapshot of the
 * document with every message and is told, through the run context, that it
 * is running in Word; the backend swaps its tool set on that. Threads are
 * keyed to this document, so reopening the file reconnects to its history.
 */
import { loadAppliedTemplate, saveAppliedTemplate } from "../../document/identity";
import { fetchTemplateStyleMap } from "../../tender/api";
import { setTemplateStyleMap } from "../../document/revisionOoxml";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuthHeader, useAuthUser, useSignOut } from "react-auth-kit";
import { Navigate, useNavigate } from "react-router-dom";
import { useAui, useAuiState } from "@assistant-ui/react";
import { useLangGraphInterruptState } from "@assistant-ui/react-langgraph";

import { uploadAgentFiles } from "@/agents-v2/api";
import { AgentFileAttachmentAdapter } from "@/agents-v2/attachments/agent-file-attachment-adapter";
import {
  BID_AGENT_DEFAULT_TOOLS,
  bidAgentRunContextSelectedTools,
} from "@/agents-v2/components/agents/bid-agent-sources";
import { ChatProvider, useAgentsChat } from "@/agents-v2/components/common/chat-provider";
import { Thread, ThreadBanner, type ThreadSuggestion } from "@/agents-v2/components/assistant-ui/thread";
import type { AgentThreadMetadata } from "@/agents-v2/chat-runtime/types";
import { Tabs, TabsList, TabsTrigger } from "@/design/ui/tabs";
import { TooltipProvider } from "@/design/ui/tooltip";

import type { ITenderBid } from "../../../types";
import {
  fallbackDocumentIdentity,
  loadDocumentIdentity,
  saveLinkedBid,
  type DocumentIdentity,
} from "../../document/identity";
import { useDocumentSnapshot } from "../../document/useDocumentSnapshot";
import { mytenderUrl, openExternal } from "../../document/openExternal";
import { suggestTender } from "../../document/suggestTender";
import { scopeFor } from "../../document/review";
import { parseWordToolInterrupt, useWordToolInterrupts } from "../../document/useWordToolInterrupts";
import { authoredRevisionsActive } from "../../document/wordTools";
import useTenderBids from "../../hooks/useTenderBids";
import { CreateTenderScreen } from "../../screens/CreateTenderScreen";
import { HomeScreen } from "../../screens/HomeScreen";
import { CapabilitiesSheet, limitsNote } from "../../screens/CapabilitiesSheet";
import { MappingSheet } from "../../screens/MappingSheet";
import { PushSheet } from "../../screens/PushSheet";
import { QuestionPicker } from "../../screens/QuestionPicker";
import { QuestionSheet } from "../../screens/QuestionSheet";
import { SendForReviewSheet } from "../../screens/SendForReviewSheet";
import { TemplateSheet } from "../../screens/TemplateSheet";
import { TenderTab, Toast } from "../../screens/TenderTab";
import {
  assignSelection,
  documentOutlineForMapping,
  highlightBlock,
  releaseForeignBlocks,
  showBlock,
} from "../../document/questionBlocks";
import { type Question, type SectionStatus, startReview } from "../../tender/questionsApi";
import { type PullProgress, type PushPlan, questionRef, useQuestions } from "../../tender/useQuestions";
import { ChatPushCard, type ChatPushState } from "./ChatPushCard";
import { completeCreation, findCreationThread, readPendingCreation } from "../../tender/createTender";

// How the pane asks for a creator thread the backend has not made yet: every
// few seconds at first, backing off to half a minute, and giving up after an
// hour or so, since a setup that failed before its thread existed leaves
// `creation_flow` on "agent" for good.
const CREATION_THREAD_RETRY_MS = 3000;
const CREATION_THREAD_RETRY_MAX_MS = 30_000;
const CREATION_THREAD_RETRY_ATTEMPTS = 120;
import { useTender } from "../../tender/useTender";
import {
  BidCreationCompleteToolUI,
  BidSetupProgressToolUI,
  CREATION_INLINE_TOOL_NAMES,
  CreationActionsContext,
} from "./creationToolUIs";
import { BidMetadataProposalToolUI } from "@/agents-v2/components/tool-ui/bid-metadata-proposal-tool-ui";
import { OutlineProposalToolUI } from "@/agents-v2/components/tool-ui/outline-proposal-tool-ui";
import { ProposalEnvProvider } from "@/agents-v2/components/tool-ui/proposal-env";
import { attachRecipeId, getRecipeId, publishRecipeId } from "@/agents-v2/recipe-metadata/recipe-metadata";
import type { LangChainMessage } from "@assistant-ui/react-langgraph";
import { WorkspaceHeader } from "../../screens/WorkspaceHeader";
import { ContextChips, type ContextChip } from "./ContextChips";
import { ConversationBar } from "./ConversationBar";
import { ImprovePill } from "./ImprovePill";
import { ReviewStrip } from "./ReviewStrip";
import { SuggestionsBar, useSuggestions } from "./SuggestionsBar";
import { SourcesControl } from "./SourcesControl";
import { WORD_RECIPES } from "./wordRecipes";

export const BID_AGENT_ID = "bid_agent";

/** The suggestions bar reviews only mytender.io's tracked changes. */
const MYTENDER_SCOPE = scopeFor(true);

/** How long the pointer rests on a question row before its answer is outlined in Word. */
const HOVER_DWELL_MS = 250;

/** How long "Show in document" keeps the answer's box drawn. */
const REVEAL_MS = 6000;

/** How often the Tender tab re-reads which answers are in the document. */
const BLOCKS_POLL_MS = 4000;
/** Answers that differ from what mytender last saw before the pane suggests sending. */
const SEND_NUDGE_AFTER = 2;
/** How often the pane re-compares the document with the sync record while the chat shows. */
const CHANGED_POLL_MS = 20000;

/**
 * A recipe sent from the composer tags its human message with the recipe id,
 * as the web app does (the scoring middleware and the eval board read it).
 * Consumed here, when the runtime prepares the outgoing messages.
 */
const attachPendingRecipe = (_threadId: string, messages: LangChainMessage[]): LangChainMessage[] => {
  const recipeId = getRecipeId();
  if (!recipeId) return messages;
  publishRecipeId(null);
  return attachRecipeId(messages, recipeId);
};
// Aegra sits behind the backend: the API checks the session and streams
// /agents/api/* through, so the pane needs no second base URL.
const LANGGRAPH_API_URL = `${(process.env.REACT_APP_API_URL || "").replace(/\/+$/, "")}/agents/api`;

/**
 * The document-template feature (Template… in the tender menu, the template
 * sheet, the offer on first link, the chat chips) is hidden for now. The
 * agent's template tools stay; asking in chat still works.
 */
const TEMPLATES_ENABLED = false;

/** The empty-chat chips, one per thing the pane does: linked to a tender, and not. */
const LINKED_SUGGESTIONS: (ThreadSuggestion & { template?: boolean })[] = [
  {
    title: "Get my answers from mytender",
    prompt: "Get every question's answer from mytender into this document and tell me what arrived.",
  },
  {
    title: "Apply our template to this document",
    prompt:
      "Apply our organisation's document template to this document: styles, cover and closing pages, headers and footers, and page setup. Tell me what landed.",
    template: true,
  },
  {
    title: "Review this section against the criteria",
    prompt:
      "Review the current region of my document against the tender's evaluation criteria and tell me what is missing or weak.",
  },
  {
    title: "Send my changes to mytender",
    prompt: "Send the answers in this document to mytender. Show me what will be sent first.",
  },
];
const UNLINKED_SUGGESTIONS: (ThreadSuggestion & { template?: boolean })[] = [
  {
    title: "Draft the section under this heading",
    prompt:
      "Draft the section under the current heading of my document from our content library, as tracked changes in our tone of voice.",
  },
  {
    title: "Apply our template to this document",
    prompt:
      "Apply our organisation's document template to this document: styles, cover and closing pages, headers and footers, and page setup. Tell me what landed.",
    template: true,
  },
  {
    title: "Tighten the selected text",
    prompt:
      "Tighten the selected text: remove repetition and filler, keep every claim and figure, as a tracked change.",
  },
];

type OmittableContext = "selection" | "section";

const templatePromptKey = (documentId: string) => `mytender_template_prompted:${documentId}`;
/** Whether this document has been offered the template yet (once per document). */
const claimTemplatePrompt = (documentId: string | null): boolean => {
  if (!documentId) return false;
  try {
    if (localStorage.getItem(templatePromptKey(documentId))) return false;
    localStorage.setItem(templatePromptKey(documentId), new Date().toISOString());
    return true;
  } catch {
    return false;
  }
};

const useBearerToken = (): string | null => {
  const getAuthHeader = useAuthHeader();
  const raw = getAuthHeader();
  return typeof raw === "string" && raw ? raw.replace(/^Bearer\s+/i, "") : null;
};

/** The document's file name without extension, when Office knows it. */
const documentFileName = (): string | null => {
  try {
    const url = Office?.context?.document?.url;
    if (!url) return null;
    return decodeURIComponent(url.split(/[\\/]/).pop() ?? "").replace(/\.docx?$/i, "") || null;
  } catch {
    return null;
  }
};

export const WordAgentPane: FC = () => {
  const token = useBearerToken();
  const navigate = useNavigate();
  const signOut = useSignOut();
  const getAuth = useAuthUser();
  const email = (getAuth()?.email as string | undefined) ?? null;

  const [identity, setIdentity] = useState<DocumentIdentity | null>(null);
  useEffect(() => {
    // The applied template's styles, so everything written into the
    // document from here on (pulled answers, io.ai's drafts) takes them. A
    // document whose template was applied before the map existed fetches
    // it from the server once and keeps it on the record.
    let cancelled = false;
    void loadAppliedTemplate()
      .then(async (t) => {
        if (!t) return null;
        if (t.styleMap || !token) return t.styleMap ?? null;
        const styleMap = await fetchTemplateStyleMap(t.fileId, token).catch(() => null);
        if (styleMap && !cancelled) await saveAppliedTemplate({ ...t, styleMap }).catch(() => undefined);
        return styleMap;
      })
      .then((map) => {
        if (!cancelled) setTemplateStyleMap(map);
      })
      .catch(() => setTemplateStyleMap(null));
    return () => {
      cancelled = true;
    };
  }, [token]);
  useEffect(() => {
    // A document whose settings can't be read still gets a pane: the threads
    // just won't follow the file.
    loadDocumentIdentity()
      .then(setIdentity)
      .catch((error) => {
        console.warn("Could not read the document's identity", error);
        setIdentity(fallbackDocumentIdentity());
      });
  }, []);

  const { tenderBids, loading: tendersLoading, error: tendersError, fetchTenderBids } = useTenderBids();
  const linkBid = useCallback(async (bidId: string | null) => {
    setIdentity((current) => (current ? { ...current, bidId } : current));
    try {
      await saveLinkedBid(bidId);
    } catch (error) {
      console.warn("Could not save the linked tender in the document", error);
    }
  }, []);

  const { getSnapshot, refresh, selectedText } = useDocumentSnapshot();
  // The bid's questions, reported by the workspace, for the run context.
  const questionsRef = useRef<Question[]>([]);
  const [documentTitle, setDocumentTitle] = useState<string | null>(null);
  useEffect(() => {
    void refresh().then((snapshot) => setDocumentTitle(snapshot?.title || documentFileName()));
  }, [refresh]);

  const bidId = identity?.bidId ?? null;
  const documentId = identity?.documentId ?? null;
  const tender = useMemo(() => tenderBids.find((b) => b._id === bidId) ?? null, [tenderBids, bidId]);
  const suggested = useMemo(() => suggestTender(documentTitle, tenderBids), [documentTitle, tenderBids]);

  // Home is shown while unlinked, unless the user chose to go on without a
  // tender (for this session), and whenever they ask to switch.
  const [skippedLinking, setSkippedLinking] = useState(false);
  const [switching, setSwitching] = useState(false);
  // Set when a document is linked for the first time: the workspace opens
  // on the template offer.
  const [offerTemplate, setOfferTemplate] = useState(false);
  // Creating a tender from the pane: the screen, and whether the open thread
  // is a creation thread (which keeps the creation tools available to the
  // agent).
  const [creating, setCreating] = useState(false);
  const [creationThreadOpen, setCreationThreadOpen] = useState(false);
  const showHome = switching || (!bidId && !skippedLinking);

  // Context the user has left out of the next message (chips).
  const [omitted, setOmitted] = useState<Set<OmittableContext>>(() => new Set());
  const toggleOmitted = useCallback((key: ContextChip["key"]) => {
    if (key === "tender") return;
    setOmitted((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const resetOmitted = useCallback(() => setOmitted((c) => (c.size ? new Set() : c)), []);

  // Which sources the agent may use; travels as `selected_tools` on the run
  // context and is remembered on the thread (see Workspace).
  const [selectedTools, setSelectedTools] = useState<string[]>(BID_AGENT_DEFAULT_TOOLS);
  const attachmentAdapter = useMemo(() => new AgentFileAttachmentAdapter(), []);
  const uploadFiles = useCallback(
    (threadId: string, files: readonly File[], signal?: AbortSignal) =>
      uploadAgentFiles({ threadId, files, signal, token }),
    [token]
  );

  // Awaited by the runtime before each send, so the snapshot the run carries
  // is the document as it is at that moment.
  const refreshBeforeSend = useCallback(async () => {
    await refresh({ full: true });
  }, [refresh]);

  // What the backend sees as the run's context (BidAgentContext): the bid,
  // the screen id the Word middleware keys on, and the document snapshot.
  const resolveContext = useCallback(() => {
    const snapshot = getSnapshot();
    // Every question on the bid, with its answer's paragraph range when it is
    // in the document, so io.ai can draft into or improve the right place.
    const ranges = new Map((snapshot?.questions ?? []).map((r) => [r.section_id, r]));
    const questions = questionsRef.current.length
      ? questionsRef.current.map((x) => ({
          section_id: x.section_id,
          title: [x.section_number, x.heading || x.question.slice(0, 80)].filter(Boolean).join(" "),
          start: ranges.get(x.section_id)?.start ?? null,
          end: ranges.get(x.section_id)?.end ?? null,
        }))
      : (snapshot?.questions ?? []);
    const word_document = snapshot && {
      ...snapshot,
      questions,
      selection: omitted.has("selection") ? null : snapshot.selection,
      region: omitted.has("section") ? null : snapshot.region,
    };
    return {
      ...(bidId && { bid_id: bidId }),
      active_view: "word",
      ui: {
        screen: {
          id: "word",
          label: "Microsoft Word",
          description: "The user's bid response, open in the mytender.io Word add-in.",
        },
        ...(creationThreadOpen && { creating_bid: true }),
      },
      word_document,
      ...(bidAgentRunContextSelectedTools(selectedTools) !== undefined && {
        selected_tools: bidAgentRunContextSelectedTools(selectedTools),
      }),
    };
  }, [bidId, getSnapshot, selectedTools, omitted, creationThreadOpen]);

  // Threads belong to this document (and its bid); the web app's threads for
  // the same bid stay separate.
  const threadScopeMetadata = useMemo<AgentThreadMetadata | undefined>(
    () => (documentId ? { surface: "word", document_id: documentId } : undefined),
    [documentId]
  );
  const initialMetadata = useMemo<AgentThreadMetadata>(
    () => ({
      surface: "word",
      ...(documentId && { document_id: documentId }),
      ...(bidId && { bid_id: bidId }),
      ...(email && { owner_email: email }),
    }),
    [documentId, bidId, email]
  );

  const onSessionExpired = useCallback(() => {
    signOut();
    navigate("/login");
  }, [signOut, navigate]);
  const onSignOut = () => navigate("/logout");
  const openMytender = () => openExternal(mytenderUrl(bidId, bidId ? "proposal-preview" : null));

  if (!token) return <Navigate to="/login" replace />;
  if (!identity) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-fg-3">
        <span className="size-2 animate-pulse rounded-full bg-accent" />
        <span className="ml-2">Connecting to the document…</span>
      </div>
    );
  }

  if (creating) {
    return (
      <TooltipProvider delayDuration={200}>
        <CreateTenderScreen
          token={token}
          documentId={documentId}
          email={email}
          suggestedTitle={documentTitle}
          pending={readPendingCreation(documentId)}
          onBack={() => setCreating(false)}
          onStarted={(newBidId) => {
            void linkBid(newBidId);
            void fetchTenderBids();
            setCreating(false);
            setSwitching(false);
            setSkippedLinking(false);
          }}
        />
      </TooltipProvider>
    );
  }

  if (showHome) {
    return (
      <TooltipProvider delayDuration={200}>
        <HomeScreen
          tenders={tenderBids}
          loading={tendersLoading}
          error={tendersError}
          suggested={suggested}
          email={email}
          currentBidId={switching ? bidId : null}
          onLink={(id) => {
            void linkBid(id);
            setSwitching(false);
            setSkippedLinking(false);
            if (TEMPLATES_ENABLED && claimTemplatePrompt(documentId)) setOfferTemplate(true);
          }}
          onCreate={() => setCreating(true)}
          onSkip={() => (switching ? setSwitching(false) : setSkippedLinking(true))}
          onRetry={() => void fetchTenderBids()}
          onSignOut={onSignOut}
          onOpenMytender={openMytender}
        />
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <ChatProvider
        // A different document or bid is a different thread scope; remounting
        // is the simplest way to reset the runtime's thread list.
        key={`${documentId}:${bidId ?? ""}`}
        assistantId={BID_AGENT_ID}
        apiUrl={LANGGRAPH_API_URL}
        token={token}
        resolveContext={resolveContext}
        onBeforeSend={refreshBeforeSend}
        initialMetadata={initialMetadata}
        threadScopeMetadata={threadScopeMetadata}
        onSessionExpired={onSessionExpired}
        prepareMessages={attachPendingRecipe}
        section={bidId ? "Word" : ""}
        attachmentAdapter={attachmentAdapter}
        uploadFiles={uploadFiles}
      >
        <Workspace
          bidId={bidId}
          token={token}
          tender={tender}
          email={email}
          offerTemplate={offerTemplate}
          onTemplateOffered={() => setOfferTemplate(false)}
          apiUrl={LANGGRAPH_API_URL}
          onCreationThreadChange={setCreationThreadOpen}
          onCreationFinished={() => void fetchTenderBids()}
          setupInProgress={tender?.creation_flow === "agent"}
          selectedText={selectedText}
          omitted={omitted}
          onToggleOmitted={toggleOmitted}
          onRunSettled={resetOmitted}
          selectedTools={selectedTools}
          onSelectedToolsChange={setSelectedTools}
          onSwitchTender={() => setSwitching(true)}
          onUnlink={() => {
            void linkBid(null);
            setSkippedLinking(false);
          }}
          onOpenMytender={openMytender}
          onSignOut={onSignOut}
          onDocumentChanged={() => void refresh()}
          onQuestions={(qs) => {
            questionsRef.current = qs;
          }}
        />
      </ChatProvider>
    </TooltipProvider>
  );
};

const Workspace: FC<{
  bidId: string | null;
  token: string;
  tender: ITenderBid | null;
  email: string | null;
  offerTemplate: boolean;
  onTemplateOffered: () => void;
  apiUrl: string;
  onCreationThreadChange: (creation: boolean) => void;
  onCreationFinished: () => void;
  /** The linked bid is still being set up by its creator conversation. */
  setupInProgress: boolean;
  selectedText: string | null;
  omitted: Set<OmittableContext>;
  onToggleOmitted: (key: ContextChip["key"]) => void;
  onRunSettled: () => void;
  selectedTools: string[];
  onSelectedToolsChange: (next: string[]) => void;
  onSwitchTender: () => void;
  onUnlink: () => void;
  onOpenMytender: () => void;
  onSignOut: () => void;
  onDocumentChanged: () => void;
  onQuestions: (questions: Question[]) => void;
}> = ({
  bidId,
  token,
  tender,
  email,
  offerTemplate,
  onTemplateOffered,
  apiUrl,
  onCreationThreadChange,
  onCreationFinished,
  setupInProgress,
  selectedText,
  omitted,
  onToggleOmitted,
  onRunSettled,
  selectedTools,
  onSelectedToolsChange,
  onSwitchTender,
  onUnlink,
  onOpenMytender,
  onSignOut,
  onDocumentChanged,
  onQuestions,
}) => {
  const {
    cancelActiveRun,
    corruptedThreadId,
    clearCorruptedThread,
    sessionExpired,
    currentThreadId,
    currentMetadata,
    updateCurrentThreadMetadata,
  } = useAgentsChat();

  // The sources selection lives on the thread: restore it when a thread opens,
  // write it when the user changes it.
  // The metadata arrives after the thread id, so this runs again when it
  // does; a thread that remembers nothing gets the defaults, not the previous
  // thread's choice.
  useEffect(() => {
    const remembered = currentMetadata.selected_tools;
    onSelectedToolsChange(
      Array.isArray(remembered) ? remembered.filter((t) => typeof t === "string") : BID_AGENT_DEFAULT_TOOLS
    );
  }, [currentThreadId, currentMetadata]);
  const changeSources = (next: string[]) => {
    onSelectedToolsChange(next);
    void updateCurrentThreadMetadata({ selected_tools: next }).catch((error) =>
      console.warn("Could not remember the sources on the thread", error)
    );
  };

  // Removed context chips come back once the message they were removed for
  // has been answered (a resume mid-run keeps the same context).
  const isRunning = useAuiState((s) => s.thread.isRunning);
  const wasRunningRef = useRef(false);
  useEffect(() => {
    if (wasRunningRef.current && !isRunning) onRunSettled();
    wasRunningRef.current = isRunning;
  }, [isRunning, onRunSettled]);
  // io.ai is editing while it runs or waits on a Word tool. Paused on a
  // question to the user, or on its own push confirmation, it is not, so a
  // push does not wait for it.
  const interrupt = useLangGraphInterruptState();
  const wordRequest = parseWordToolInterrupt(interrupt?.value);
  const agentEditing = isRunning || Boolean(wordRequest && wordRequest.tool !== "push_to_mytender");

  // Chat · Tender. The thread stays mounted behind the Tender tab so a run
  // in flight keeps streaming.
  const [tab, setTab] = useState<"chat" | "tender">("chat");
  // A pull reports on the Tender tab: progress while it runs, then a result
  // line that stays until dismissed (a toast times out long before 22
  // questions have landed). While it runs, the pane's own periodic reads of
  // the document pause so the pull's writes don't queue behind them.
  const [pullProgress, setPullProgress] = useState<PullProgress | null>(null);
  const [pullResult, setPullResult] = useState<string | null>(null);
  const pulling = pullProgress !== null;
  // A result belongs to the bid it came from.
  useEffect(() => setPullResult(null), [bidId]);
  const details = useTender(bidId, token);
  const aui = useAui();
  const [documentNotice, setDocumentNotice] = useState<{ tone: "info" | "error"; text: string } | null>(null);

  // Creation threads: find the bid's while the linked bid is mid-setup and
  // open it, and tell the pane while one is open. The backend creates the
  // thread once the uploaded documents have landed, so a bid whose setup
  // just started is asked again until the thread exists.
  const isCreationThread = currentMetadata.creation === true || currentMetadata.creation === "true";
  useEffect(() => {
    onCreationThreadChange(isCreationThread);
  }, [isCreationThread, onCreationThreadChange]);
  const openedForBidRef = useRef<string | null>(null);
  useEffect(() => {
    const open = async (threadId: string) => {
      try {
        await aui.threads().switchToThread(threadId);
      } catch (error) {
        console.warn("Could not open the creation thread", error);
      }
    };
    if (!setupInProgress || !bidId || openedForBidRef.current === bidId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    const seek = async () => {
      const threadId = await findCreationThread(apiUrl, token, bidId).catch(() => null);
      if (cancelled) return;
      if (threadId) {
        openedForBidRef.current = bidId;
        await open(threadId);
        return;
      }
      attempt += 1;
      if (attempt >= CREATION_THREAD_RETRY_ATTEMPTS) return;
      const delay = Math.min(CREATION_THREAD_RETRY_MS * 2 ** Math.floor(attempt / 5), CREATION_THREAD_RETRY_MAX_MS);
      timer = setTimeout(() => void seek(), delay);
    };
    void seek();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [setupInProgress, bidId, apiUrl, token, aui]);
  const creationActions = useMemo(
    () => ({
      onFinish: async () => {
        if (!bidId) return;
        await completeCreation(bidId, token);
        onCreationFinished();
        await details.refresh();
        setDocumentNotice({
          tone: "info",
          text: "Setup finished. Get from mytender on the Tender tab brings the questions into this document.",
        });
      },
    }),
    [bidId, token, onCreationFinished, details.refresh]
  );
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateFirstLink, setTemplateFirstLink] = useState(false);
  useEffect(() => {
    if (offerTemplate) {
      setTemplateFirstLink(true);
      setTemplateOpen(true);
      onTemplateOffered();
    }
  }, [offerTemplate, onTemplateOffered]);

  // ---- Questions: mytender's list joined with this document's answer blocks ----
  const q = useQuestions(bidId, token);
  useEffect(() => onQuestions(q.questions), [q.questions, onQuestions]);
  const selectionWords = selectedText ? (selectedText.match(/\S+/g) ?? []).length : 0;
  const [openQuestionId, setOpenQuestionId] = useState<string | null>(null);
  const openQuestion = q.questions.find((x) => x.section_id === openQuestionId) ?? null;
  const [pushTarget, setPushTarget] = useState<{ ids?: string[] } | null>(null);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Send for review: the web app's dialog, in the pane.
  const [reviewFor, setReviewFor] = useState<Question | null>(null);
  const [capsOpen, setCapsOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(timer);
  }, [toast]);
  useEffect(() => {
    // Both may have changed while the tab was hidden: the document (io.ai's
    // edits, the writer typing) and mytender (statuses, answers, limits).
    // Not during a pull: its writes must not queue behind a read.
    if (tab === "tender" && !pulling) void q.refresh();
  }, [tab]);
  // While the Tender tab shows, keep "in document" current with what happens
  // in Word itself (a rejected pull removes its block; typing changes word
  // counts): re-read the blocks every few seconds while the pane is visible.
  // The same tick also asks mytender for the questions (a limit or status
  // changed on the platform lands here within seconds, without switching
  // tabs), row by row, never as a reload: see useQuestions.syncFromServer.
  const refreshBlocks = q.refreshBlocks;
  const syncFromServer = q.syncFromServer;
  useEffect(() => {
    if (tab !== "tender") return;
    if (pulling) return;
    const timer = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      void refreshBlocks();
      void syncFromServer();
    }, BLOCKS_POLL_MS);
    return () => clearInterval(timer);
  }, [tab, pulling, refreshBlocks, syncFromServer]);
  // An accepted proposal changed the outline in mytender: read it back now.
  const proposalApplied = useCallback(() => {
    void q.refresh();
  }, [q.refresh]);

  // Resting on a row outlines its answer in the document (no text is
  // written). Each outline is an appearance change Word keeps in its undo
  // history, so passing over rows on the way somewhere outlines nothing:
  // only a pause of HOVER_DWELL_MS does.
  const outlinedRef = useRef<string | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onHover = useCallback(
    (id: string | null) => {
      if (!bidId) return;
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      const clear = () => {
        const prev = outlinedRef.current;
        outlinedRef.current = null;
        if (prev) void highlightBlock(bidId, prev, false);
      };
      if (!id) {
        clear();
        return;
      }
      if (outlinedRef.current === id) return;
      hoverTimerRef.current = setTimeout(() => {
        clear();
        outlinedRef.current = id;
        void highlightBlock(bidId, id, true);
      }, HOVER_DWELL_MS);
    },
    [bidId]
  );
  // Leaving the Tender tab mid-hover must not leave an outline behind.
  useEffect(() => {
    if (tab !== "tender") onHover(null);
  }, [tab, onHover]);
  // "Show in document": scroll to the answer with its box drawn; the box goes
  // when a row is next hovered, or by itself after a moment.
  const revealBlock = useCallback(
    (id: string) => {
      if (!bidId) return;
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      const prev = outlinedRef.current;
      if (prev && prev !== id) void highlightBlock(bidId, prev, false);
      outlinedRef.current = id;
      void showBlock(bidId, id);
      setTimeout(() => {
        if (outlinedRef.current === id) {
          outlinedRef.current = null;
          void highlightBlock(bidId, id, false);
        }
      }, REVEAL_MS);
    },
    [bidId]
  );

  // A document written before has text but no question blocks: connect it first.
  const needsMapping = async (): Promise<boolean> => {
    if (q.blocks.size > 0) return false;
    try {
      return (await documentOutlineForMapping()).length > 3;
    } catch {
      return false;
    }
  };

  const summarisePull = (outcomes: { status: string }[]) => {
    const replaced = outcomes.filter((o) => o.status === "replaced").length;
    const added = outcomes.filter((o) => o.status === "added").length;
    const empty = outcomes.filter((o) => o.status === "empty").length;
    const unchanged = outcomes.filter((o) => o.status === "unchanged").length;
    const failed = outcomes.filter((o) => o.status === "error").length;
    if (!replaced && !added && !failed) {
      return unchanged
        ? `Already up to date with mytender: ${unchanged} answer${unchanged === 1 ? "" : "s"} unchanged${empty ? `, ${empty} with no answer in mytender yet` : ""}.`
        : "Nothing to get: no answers in mytender yet.";
    }
    return (
      [
        `Got ${replaced + added} question${replaced + added === 1 ? "" : "s"} from mytender`,
        replaced ? `${replaced} replaced` : null,
        added ? `${added} added in question order` : null,
        unchanged ? `${unchanged} already up to date` : null,
        empty ? `${empty} kept (no answer in mytender yet)` : null,
        failed ? `${failed} couldn't be written` : null,
      ]
        .filter(Boolean)
        .join(", ") + (replaced ? ". Replaced answers are suggestions to review." : ".")
    );
  };

  const runPull = async (ids?: string[]) => {
    setPullResult(null);
    setTab("tender");
    try {
      const outcomes = await q.pull(ids, setPullProgress);
      setPullResult(summarisePull(outcomes));
    } catch (error) {
      setPullResult(
        `Couldn't get answers: ${error instanceof Error ? error.message : "Word or mytender didn't respond."}`
      );
    } finally {
      setPullProgress(null);
      onDocumentChanged();
    }
  };
  const pullAll = async () => {
    if (await needsMapping()) {
      setMappingOpen(true);
      return;
    }
    await runPull();
  };
  const pullOne = async (id: string) => {
    await runPull([id]);
  };
  const openPush = async (ids?: string[]) => {
    if (await needsMapping()) {
      setMappingOpen(true);
      return;
    }
    setPushTarget({ ids });
  };
  const useSelectionFor = async (question: Question) => {
    if (!bidId) return;
    const out = await assignSelection(bidId, questionRef(question));
    await q.refreshBlocks();
    onDocumentChanged();
    setToast(
      out.status === "moved"
        ? `Moved to the end of ${question.heading}. Review the change in the document.`
        : out.status === "taken"
          ? `Moved into a new answer to ${question.heading}, after the answer it was in. Review the change in the document.`
          : out.status === "wrapped"
            ? `The selection is now the answer to ${question.heading}.`
            : (out.message ?? "Couldn't add the selection.")
    );
  };
  const changeStatus = async (question: Question, status: SectionStatus) => {
    // Sending for review picks reviewers and emails them: the sheet, not a
    // silent status change.
    if (status === "Awaiting review") {
      setReviewFor(question);
      return;
    }
    try {
      const { emailed } = await q.changeStatus(question.section_id, status);
      if (status === "Complete")
        setToast(emailed ? `Marked complete. ${emailed}, the answerer, was emailed.` : "Marked complete.");
    } catch (error) {
      const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setToast(typeof detail === "string" ? detail : "Couldn't change the status. Try again.");
    }
  };

  // ---- io.ai's pull and push tools ----
  const [chatPush, setChatPush] = useState<ChatPushState | null>(null);
  const chatPushResolve = useRef<((value: unknown) => void) | null>(null);
  const chatPushPlan = useRef<PushPlan | null>(null);
  const confirmPush = (ids: string[]) =>
    new Promise<unknown>((resolve) => {
      chatPushResolve.current = resolve;
      setChatPush({ phase: "planning", count: ids.length });
      q.planPush(ids.length ? ids : undefined)
        .then((plan) => {
          chatPushPlan.current = plan;
          setChatPush({ phase: "confirm", plan });
        })
        .catch((error) => {
          setChatPush(null);
          chatPushResolve.current = null;
          resolve({ status: "error", message: error instanceof Error ? error.message : "Couldn't read the document" });
        });
    });
  const confirmChatPush = async () => {
    const plan = chatPushPlan.current;
    if (!plan) return;
    setChatPush({ phase: "pushing", plan });
    const out = await q.push(plan.rows);
    const title = (id: string) => q.questions.find((x) => x.section_id === id)?.heading ?? id;
    const pushed = out.results.filter((r) => r.status === "pushed").map((r) => title(r.section_id));
    const failed = out.results.filter((r) => r.status === "failed").map((r) => title(r.section_id));
    const time = new Date().toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    setChatPush({
      phase: "done",
      failed: failed.length > 0,
      text: `Sent ${pushed.join(", ") || "nothing"}${failed.length ? ` · failed: ${failed.join(", ")}` : ""} · ${time}`,
    });
    chatPushResolve.current?.({ status: "done", pushed, failed });
    chatPushResolve.current = null;
  };
  const cancelChatPush = () => {
    chatPushResolve.current?.({ status: "cancelled", message: "The user cancelled. Nothing was sent." });
    chatPushResolve.current = null;
    setChatPush(null);
  };

  // The agent's document tools pause the run until the pane answers them.
  const [nudgeSeenAt, setNudgeSeenAt] = useState(0);
  const { notice: editNotice, dismiss: dismissEditNotice } = useWordToolInterrupts(
    {
      bidId,
      pull: async (ids, directives) => {
        const outcomes = await q.pull(ids.length ? ids : undefined, undefined, directives);
        onDocumentChanged();
        return outcomes;
      },
      confirmPush,
      writeAnswer: q.writeAnswer,
      showQuestion: async (id) => {
        const question = q.questions.find((x) => x.section_id === id);
        if (!question) return { status: "unknown_question", message: "No question with that section_id on this bid." };
        const title = [question.section_number, question.heading || question.question.slice(0, 80)]
          .filter(Boolean)
          .join(" ");
        const block = q.blocks.get(id);
        if (!block) return { status: "not_in_document", title, question_status: question.status };
        revealBlock(id);
        return { status: "shown", title, question_status: question.status, words_in_document: block.words };
      },
    },
    onDocumentChanged
  );

  // mytender.io's suggestions in the document (pulls and io.ai's edits).
  // Always scoped to that author: a shared document also holds colleagues'
  // tracked changes, which Accept all must never touch.
  const reviewScope = MYTENDER_SCOPE;
  const suggestions = useSuggestions(reviewScope, pulling);
  const refreshSuggestions = suggestions.refresh;
  useEffect(() => {
    void refreshSuggestions();
  }, [editNotice, tab, q.blocks, refreshSuggestions]);
  // The "send to mytender" nudge compares the document with the sync record:
  // after an edit lands, after a review action, and on a slow tick so a
  // change the writer removed by hand stops counting too.
  const recountChanged = q.recountChanged;
  useEffect(() => {
    if (editNotice && editNotice.applied > 0) void recountChanged();
  }, [editNotice, recountChanged]);
  useEffect(() => {
    if (!bidId) return;
    void recountChanged();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") void recountChanged();
    }, CHANGED_POLL_MS);
    return () => clearInterval(timer);
  }, [bidId, recountChanged]);
  // A change in the suggestion count means the document changed (accepted or
  // rejected in Word, or new edits): the blocks may have too.
  const suggestionCount = suggestions.count;
  useEffect(() => {
    void refreshBlocks();
  }, [suggestionCount, refreshBlocks]);
  const suggestionsBar = (
    <SuggestionsBar
      suggestions={suggestions.suggestions}
      scope={reviewScope}
      onChanged={() => {
        void suggestions.refresh();
        void q.refreshBlocks();
        void q.recountChanged();
        onDocumentChanged();
      }}
    />
  );

  const limits = limitsNote();
  const tenderBanner =
    q.otherBidId || limits ? (
      <div className="mx-3 mt-2.5 flex flex-col gap-2">
        {q.otherBidId && (
          <div className="rounded-sm border border-accent-soft-2 bg-accent-soft px-2.5 py-2 text-xs leading-[17px] text-fg">
            This document&apos;s answers belong to another bid. Connect them to this one to pull and push here.{" "}
            <button
              type="button"
              className="font-semibold text-accent-hover hover:underline"
              onClick={async () => {
                if (!bidId) return;
                await releaseForeignBlocks(bidId);
                await q.refreshBlocks();
                setMappingOpen(true);
              }}
            >
              Connect
            </button>
          </div>
        )}
        {limits && (
          <div className="rounded-sm border border-accent-soft-2 bg-accent-soft px-2.5 py-2 text-xs leading-[17px] text-fg">
            {limits}{" "}
            <button
              type="button"
              className="font-semibold text-accent-hover hover:underline"
              onClick={() => setCapsOpen(true)}
            >
              Details
            </button>
          </div>
        )}
      </div>
    ) : null;

  const chips = useMemo<ContextChip[]>(() => {
    const words = selectedText ? (selectedText.match(/\S+/g) ?? []).length : 0;
    // One chip, and only when text is selected in Word: io.ai will read that
    // selection with the next message; × leaves it out of that message.
    return words
      ? [
          {
            key: "selection" as const,
            label: `Selected text · ${words} ${words === 1 ? "word" : "words"}`,
            removable: true,
            removed: omitted.has("selection"),
          },
        ]
      : [];
  }, [selectedText, omitted]);

  // The review bar sits over the input while changes wait for review (below);
  // notices stay under the composer.
  const nudge = q.changedSinceSend >= SEND_NUDGE_AFTER && q.changedSinceSend !== nudgeSeenAt;
  const footer = (
    <>
      {editNotice && (
        <EditNoticeStrip
          notice={editNotice}
          reviewInBar={suggestions.live}
          onDismiss={dismissEditNotice}
          onDocumentChanged={onDocumentChanged}
        />
      )}
      {documentNotice && (
        <ThreadBanner tone={documentNotice.tone}>
          <span>{documentNotice.text}</span>{" "}
          <button type="button" className="underline" onClick={() => setDocumentNotice(null)}>
            Dismiss
          </button>
        </ThreadBanner>
      )}
      {sessionExpired && <ThreadBanner tone="error">Your session has expired. Sign in again to continue.</ThreadBanner>}
      {corruptedThreadId && (
        <ThreadBanner tone="error">
          This conversation can&apos;t continue.{" "}
          <button type="button" className="underline" onClick={clearCorruptedThread}>
            Start a new chat
          </button>
          .
        </ThreadBanner>
      )}
    </>
  );

  return (
    <div className="flex h-full flex-col bg-surface">
      <WorkspaceHeader
        tender={tender}
        bidId={bidId}
        email={email}
        onSwitch={onSwitchTender}
        onUnlink={onUnlink}
        onOpenMytender={onOpenMytender}
        onOpenTemplate={
          TEMPLATES_ENABLED
            ? () => {
                setTemplateFirstLink(false);
                setTemplateOpen(true);
              }
            : undefined
        }
        onSignOut={onSignOut}
        center={
          bidId ? (
            <Tabs value={tab} onValueChange={(value) => setTab(value as "chat" | "tender")}>
              <TabsList className="h-6 p-0.5">
                <TabsTrigger value="chat" className="h-5 px-2.5 text-xs">
                  Chat
                </TabsTrigger>
                <TabsTrigger value="tender" className="h-5 px-2.5 text-xs">
                  Tender
                </TabsTrigger>
              </TabsList>
            </Tabs>
          ) : null
        }
      />
      <div className={tab === "tender" ? "min-h-0 flex-1" : "hidden"}>
        {bidId && (
          <TenderTab
            tender={details.tender}
            tenderLoading={details.loading}
            tenderError={details.error}
            onRetry={() => {
              void details.refresh();
              void q.refresh();
            }}
            q={q}
            banner={tenderBanner}
            onOpenQuestion={(question) => {
              setOpenQuestionId(question.section_id);
              // Opening a question shows its answer in the document too.
              if (q.blocks.has(question.section_id)) revealBlock(question.section_id);
            }}
            onHover={onHover}
            onPushAll={() => void openPush()}
            onPullAll={() => void pullAll()}
            pullProgress={pullProgress}
            pullResult={pullResult}
            onDismissPullResult={() => setPullResult(null)}
            footer={suggestions.count > 0 ? suggestionsBar : null}
          />
        )}
      </div>
      <div className={tab === "chat" ? "flex min-h-0 flex-1 flex-col" : "hidden"}>
        <ConversationBar />
        <div className="min-h-0 flex-1">
          <CreationActionsContext.Provider value={creationActions}>
            <Thread
              toolUIs={
                <>
                  <BidSetupProgressToolUI />
                  <BidCreationCompleteToolUI />
                  <ProposalEnvProvider token={token} bidId={bidId} notify={setToast} onApplied={proposalApplied}>
                    <OutlineProposalToolUI />
                    <BidMetadataProposalToolUI />
                  </ProposalEnvProvider>
                </>
              }
              inlineToolNames={CREATION_INLINE_TOOL_NAMES}
              welcome={<Welcome tender={tender} linked={Boolean(bidId)} />}
              suggestions={(bidId ? LINKED_SUGGESTIONS : UNLINKED_SUGGESTIONS).filter(
                (s) => TEMPLATES_ENABLED || !s.template
              )}
              footer={footer}
              aboveComposer={
                <div className="relative">
                  {suggestions.count > 0 && <div className="mb-2">{suggestionsBar}</div>}
                  {nudge && bidId && !chatPush && (
                    <div
                      role="status"
                      className="mb-2 flex items-center gap-2 rounded-sm border border-accent/30 bg-accent-soft px-2.5 py-1.5 text-xs text-fg"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="font-semibold">{q.changedSinceSend}</span> answer
                        {q.changedSinceSend === 1 ? "" : "s"} changed since mytender last saw them.
                      </span>
                      <button
                        type="button"
                        className="font-semibold text-accent-hover hover:underline"
                        onClick={() => void openPush()}
                      >
                        Send to mytender
                      </button>
                      <button
                        type="button"
                        className="text-fg-3 hover:underline"
                        onClick={() => setNudgeSeenAt(q.changedSinceSend)}
                      >
                        Later
                      </button>
                    </div>
                  )}
                  {chatPush && (
                    <ChatPushCard
                      state={chatPush}
                      onConfirm={() => void confirmChatPush()}
                      onCancel={cancelChatPush}
                      onDismiss={() => setChatPush(null)}
                    />
                  )}
                  <div className="pointer-events-none absolute -top-10 left-0 right-0 z-10 flex justify-center">
                    <ImprovePill
                      selectedText={omitted.has("selection") ? null : selectedText}
                      onAddToQuestion={bidId && q.questions.length ? () => setPickerOpen(true) : undefined}
                    />
                  </div>
                  <ContextChips chips={chips} onToggle={onToggleOmitted} />
                </div>
              }
              composerControls={
                <SourcesControl selectedTools={selectedTools} onChange={changeSources} tenderLinked={Boolean(bidId)} />
              }
              recipes={WORD_RECIPES}
              onCancel={() => void cancelActiveRun()}
            />
          </CreationActionsContext.Provider>
        </div>
      </div>
      <QuestionSheet
        question={openQuestion}
        inDocument={Boolean(openQuestion && q.blocks.has(openQuestion.section_id))}
        wordsInDocument={openQuestion ? (q.blocks.get(openQuestion.section_id)?.words ?? 0) : 0}
        selectionWords={selectionWords}
        onClose={() => setOpenQuestionId(null)}
        onChangeStatus={(status) => openQuestion && void changeStatus(openQuestion, status)}
        onSendForReview={() => openQuestion && setReviewFor(openQuestion)}
        onShowInDocument={() => openQuestion && revealBlock(openQuestion.section_id)}
        onSaveLimit={(value, unit) =>
          openQuestion ? q.updateLimit(openQuestion.section_id, value, unit) : Promise.resolve()
        }
        onSaveWritingPlan={(plan) =>
          openQuestion ? q.updateWritingPlan(openQuestion.section_id, plan) : Promise.resolve()
        }
        onDraft={(kind) => {
          if (!openQuestion) return;
          const name = [openQuestion.section_number, openQuestion.heading || openQuestion.question.slice(0, 80)]
            .filter(Boolean)
            .join(" ");
          setOpenQuestionId(null);
          setTab("chat");
          aui.thread().append({
            role: "user",
            content: [
              {
                type: "text",
                text: kind === "draft" ? `Draft the answer to ${name}.` : `Improve the answer to ${name}.`,
              },
            ],
          });
        }}
        onPull={() => {
          if (!openQuestion) return;
          const id = openQuestion.section_id;
          setOpenQuestionId(null);
          void pullOne(id);
        }}
        onPush={() => {
          if (!openQuestion) return;
          const id = openQuestion.section_id;
          setOpenQuestionId(null);
          void openPush([id]);
        }}
        onUseSelection={() => openQuestion && void useSelectionFor(openQuestion)}
        onOpened={() => {
          if (!bidId || !openQuestion) return;
          const id = openQuestion.section_id;
          void startReview(bidId, id, token)
            .then((changed) => changed && q.patch(id, { status: "In review" }))
            .catch(() => undefined);
        }}
      />
      <PushSheet
        open={Boolean(pushTarget)}
        sectionIds={pushTarget?.ids}
        q={q}
        agentRunning={agentEditing}
        onClose={() => setPushTarget(null)}
        onPullAll={() => {
          setPushTarget(null);
          void pullAll();
        }}
      />
      {bidId && (
        <MappingSheet
          open={mappingOpen}
          bidId={bidId}
          token={token}
          questions={q.questions}
          onClose={() => setMappingOpen(false)}
          onApplied={(count) => {
            setMappingOpen(false);
            void q.refreshBlocks();
            setToast(
              `${count} answer${count === 1 ? "" : "s"} connected to their questions. Nothing in the text changed.`
            );
          }}
        />
      )}
      <QuestionPicker
        open={pickerOpen}
        selectionWords={selectionWords}
        questions={q.questions}
        inDocument={(id) => q.blocks.has(id)}
        onClose={() => setPickerOpen(false)}
        onPick={async (question) => {
          await useSelectionFor(question);
          setPickerOpen(false);
        }}
      />
      <SendForReviewSheet
        question={reviewFor}
        token={token}
        onClose={() => setReviewFor(null)}
        onSend={async (payload) => {
          if (!reviewFor) return { emailed: [], skipped: [] };
          const out = await q.sendForReview(reviewFor.section_id, payload);
          setReviewFor(null);
          setToast(
            [
              out.emailed.length ? `Sent for review to ${out.emailed.join(", ")}.` : "Marked Awaiting review.",
              out.skipped.length ? `Couldn't email ${out.skipped.join(", ")}.` : null,
            ]
              .filter(Boolean)
              .join(" ")
          );
          return out;
        }}
      />
      <CapabilitiesSheet open={capsOpen} onClose={() => setCapsOpen(false)} />
      {/* Above the sheets (z-50), so a sheet's own actions can report back. */}
      {toast && (
        <div className="pointer-events-none fixed inset-x-3 bottom-3 z-[60]">
          <Toast text={toast} />
        </div>
      )}
      <TemplateSheet
        open={TEMPLATES_ENABLED && templateOpen}
        firstLink={templateFirstLink}
        token={token}
        onClose={() => setTemplateOpen(false)}
        onApply={(template) => {
          setTemplateOpen(false);
          setTab("chat");
          aui.thread().append({
            role: "user",
            content: [
              {
                type: "text",
                text: `Apply our template "${template.filename.replace(/\.docx?$/i, "")}" to this document.`,
              },
            ],
          });
        }}
      />
    </div>
  );
};

/**
 * What happened in the document after the agent's edit or comment tool ran.
 * Tracked edits get the review strip (its Accept/Reject controls arrive with
 * the review tools); everything else is a plain banner.
 */
const EditNoticeStrip: FC<{
  notice: NonNullable<ReturnType<typeof useWordToolInterrupts>["notice"]>;
  /** The suggestions bar already offers Accept/Reject all for tracked edits. */
  reviewInBar: boolean;
  onDismiss: () => void;
  onDocumentChanged: () => void;
}> = ({ notice, reviewInBar, onDismiss, onDocumentChanged }) => {
  const reviewable = notice.kind !== "comments" && notice.applied > 0 && notice.trackedChanges && !notice.failed;
  // Authored edits are in the suggestions bar; edits that landed under the
  // user's name are not in its scope, so they keep the strip.
  // ...unless some were refused and landed under the user's name: those show here.
  if (reviewable && reviewInBar && (notice.authored ?? authoredRevisionsActive()) && !notice.authoredError) return null;
  if (reviewable) {
    return (
      <ReviewStrip
        notice={notice}
        scope={scopeFor(notice.authored ?? authoredRevisionsActive())}
        onDismiss={onDismiss}
        onDocumentChanged={onDocumentChanged}
      />
    );
  }
  return (
    <ThreadBanner tone={notice.failed ? "error" : "info"}>
      <span>{describeEditNotice(notice)}</span>{" "}
      <button type="button" className="underline" onClick={onDismiss}>
        Dismiss
      </button>
    </ThreadBanner>
  );
};

const describeEditNotice = (notice: NonNullable<ReturnType<typeof useWordToolInterrupts>["notice"]>): string => {
  const parts: string[] = [];
  const n = notice.applied;
  if (notice.kind === "comments") {
    if (n) parts.push(`${n} ${n === 1 ? "comment" : "comments"} added to the document.`);
    if (!notice.trackedChanges) parts.push("This version of Word can't take comments; the findings are in the reply.");
  } else if (notice.kind === "formatting") {
    if (n) parts.push(`${n} formatting ${n === 1 ? "change" : "changes"} applied.`);
  } else if (n) {
    parts.push(
      notice.trackedChanges
        ? `${n} ${n === 1 ? "change is" : "changes are"} waiting for review as tracked changes in the document.`
        : `${n} ${n === 1 ? "change was" : "changes were"} applied directly (this version of Word can't track changes); undo with Ctrl/Cmd+Z if needed.`
    );
  }
  if (notice.stale) parts.push(`${notice.stale} skipped because the text had changed.`);
  if (notice.failed)
    parts.push(
      `${notice.failed} not applied: Word refused ${notice.failed === 1 ? "it" : "them"} as mytender.io changes${notice.authoredError ? ` (${notice.authoredError})` : ""}.`
    );
  return parts.join(" ");
};

const Welcome: FC<{ tender: ITenderBid | null; linked: boolean }> = ({ tender, linked }) => (
  <div className="flex flex-col gap-1.5">
    <span className="flex size-7 items-center justify-center rounded-sm bg-fg text-xs font-bold text-fg-inverse">
      io
    </span>
    <h1 className="mt-1 text-lg font-semibold text-fg">{tender ? tender.bid_title : "io.ai"}</h1>
    <p className="text-base text-fg-3">
      {linked
        ? "Ask about the tender, your library or the text you're working on. I can see the document and your selection."
        : "Ask about your content library or the text you're working on. Link a tender for its documents and criteria."}
    </p>
  </div>
);

export default WordAgentPane;
